import { useSearchParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";

import { Category } from "../categories/Category";
import { PropsWithClassName } from "../../common/types/PropsWithClassName";
import { fetchCategoriesByDepartment, searchCategories } from "../categories/categoriesApi";
import Label from "../../common/components/form/Label";
import RecommendedCategory from "../categories/RecommendedCategory";
import ServiceCatalogCards from "../serviceCatalog/ServiceCatalogCards";
import NoResultsFound from "../../common/components/noResultsFound/NoResultsFound";
import LoadingSpinner from "../../common/components/loadingSpinner/LoadingSpinner";

interface TicketClassifierProps extends PropsWithClassName {
  departmentId?: string;
  showAlternativeCategories?: boolean;
  onCategorySelect: (category: Category, searchText: string) => void;
}

export default function TicketClassifier({ className, departmentId, showAlternativeCategories, onCategorySelect }: TicketClassifierProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const categoriesByDepartmentIdsRef = useRef<Record<string, Category[]>>({});
  const [searchText, setSearchText] = useState(searchParams.get("searchText") ?? "");
  const [suggestedCategories, setSuggestedCategories] = useState<Category[] | null>(null);
  const [categoriesByDepartmentId, setCategoriesByDepartmentId] = useState<Record<string, Category[]>>({});
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const paramSearchText = searchParams.get("searchText") ?? "";
    if (paramSearchText.trim().length >= 2) {
      setSearchText(paramSearchText);
      doFetchRecommendedCategoriesBySearchTerm();
    }
  }, []);

  useEffect(() => {
    const loadCategories = async () => {
      if (suggestedCategories) {
        for (const category of suggestedCategories) {
          if (categoriesByDepartmentIdsRef.current[category.departmentId]) {
            setCategoriesByDepartmentId((prev) => ({
              ...prev,
              [category.departmentId]: categoriesByDepartmentIdsRef.current[category.departmentId],
            }));
            continue;
          }

          const allCategories = await fetchCategoriesByDepartment(category.departmentId);
          const activeCategories = allCategories.filter((c) => c.isActive);
          categoriesByDepartmentIdsRef.current[category.departmentId] = activeCategories;
          setCategoriesByDepartmentId((prev) => ({
            ...prev,
            [category.departmentId]: activeCategories,
          }));
        }
      }
    };
    loadCategories();
  }, [suggestedCategories]);

  const doFetchRecommendedCategoriesBySearchTerm = async () => {
    if (searchText.trim().length < 2) {
      toast.error("Căutarea necesită minimum 2 caractere");
      return;
    }
    setIsLoading(true);
    setSearchParams({ searchText });
    const categories = await searchCategories(searchText, departmentId);
    setSuggestedCategories(categories);
    setIsLoading(false);
  };

  return (
    <div className={`${className}`}>
      <div className="flex gap-2">
        <InputText
          className="flex-1"
          placeholder="Descrie problema ta..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              doFetchRecommendedCategoriesBySearchTerm();
            }
          }}
        />
        <Button icon="pi pi-search" label="Cauta" outlined loading={isLoading} onClick={() => doFetchRecommendedCategoriesBySearchTerm()} />
      </div>
      {isLoading ? (
        <div className="flex justify-content-center">
          <LoadingSpinner />
        </div>
      ) : suggestedCategories === null ? null : (
        <div className="flex flex-column gap-2 mt-5">
          {suggestedCategories.length > 0 ? (
            <>
              <Label>Alege categoria</Label>
              {suggestedCategories.map((category: Category) => (
                <RecommendedCategory
                  key={category.id}
                  category={category}
                  allCategories={categoriesByDepartmentId[category.departmentId] ?? []}
                  onCategorySelect={(category) => onCategorySelect(category, searchText)}
                />
              ))}
            </>
          ) : (
            <NoResultsFound />
          )}

          {showAlternativeCategories && (
            <div className="flex justify-content-end w-full">
              <Button
                size="small"
                icon="pi pi-book"
                text
                label={showAllCategories ? "Ascunde categoriile" : "Nu gasesti categoria? Apasa aici..."}
                onClick={() => setShowAllCategories((prev) => !prev)}
              />
            </div>
          )}
        </div>
      )}
      {showAlternativeCategories && showAllCategories && (
        <div className="py-2">
          <ServiceCatalogCards className="py-2" />
        </div>
      )}
    </div>
  );
}
